import * as React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Package,
  Info,
  Store,
  Check,
  Bell,
  Heart,
  UserCheck,
  Sparkles,
  ImageIcon,
  LayoutGrid,
  type LucideIcon,
} from 'lucide-react';

import { Card, CardContent } from '@/components/atoms/card';
import { Badge } from '@/components/atoms/badge';
import { Button } from '@/components/atoms/button';
import { Label } from '@/components/atoms/label';
import { Input } from '@/components/atoms/input';
import { Textarea } from '@/components/atoms/textarea';
import { Skeleton } from '@/components/atoms/skeleton';
import { cn, formatPrice } from '@/lib/utils';
import { getImageUrl } from '@/lib/utils/image';
import { ProductPickerDialog, type SelectedProduct } from '../../product-picker-dialog';
import type { SelectedShop } from '../../../stores/campaign-wizard.store';
import type { AdType } from '../../../types/ads.types';
import { useShops } from '@/features/shops/hooks/use-shops';
import type { Shop } from '@/features/shops/types/shops.types';
import {
  requiresProducts,
  isNotificationType,
  isFeaturedShopType,
} from '../../../utils/pricing.utils';
import { MessageTemplates } from '../shared/MessageTemplates';
import { EmojiPickerButton } from '../shared/EmojiPicker';
import { NotificationPreview } from '../../preview/NotificationPreview';

// ============ Campaign Type Config ============

interface CampaignTypeInfo {
  icon: LucideIcon;
  label: string;
  objective: string;
  audienceLabel?: string;
}

const CAMPAIGN_TYPE_INFO: Record<AdType, CampaignTypeInfo> = {
  push_shop_subscribers: {
    icon: UserCheck,
    label: 'Notifier mes abonnés',
    objective: 'Envoyer une notification push à tous les utilisateurs abonnés à votre boutique',
    audienceLabel: 'abonnés',
  },
  push_product_likers: {
    icon: Heart,
    label: 'Notifier les fans produits',
    objective: 'Envoyer une notification push aux utilisateurs qui ont aimé vos produits',
    audienceLabel: 'fans de vos produits',
  },
  push_single_product_likers: {
    icon: Bell,
    label: "Notifier fans d'un produit",
    objective: 'Envoyer une notification push aux utilisateurs qui ont aimé un produit spécifique',
    audienceLabel: 'fans du produit',
  },
  push_all_users: {
    icon: Bell,
    label: 'Notifier tous les utilisateurs',
    objective: 'Envoyer une notification push à tous les utilisateurs de la plateforme',
    audienceLabel: 'utilisateurs',
  },
  sponsored_product: {
    icon: Sparkles,
    label: 'Produit sponsorisé',
    objective: 'Mettre votre produit en avant dans les résultats de recherche',
  },
  homepage_carousel: {
    icon: ImageIcon,
    label: 'Carousel accueil',
    objective: "Afficher votre bannière sur la page d'accueil de l'application",
  },
  category_banner: {
    icon: LayoutGrid,
    label: 'Bannière catégorie',
    objective: 'Afficher une bannière publicitaire dans une catégorie de produits',
  },
  featured_shop: {
    icon: Store,
    label: 'Boutique en vedette',
    objective: 'Mettre votre boutique en avant auprès des visiteurs',
  },
};

// ============ Types ============

interface StepProductSelectionProps {
  campaignType: AdType;
  selectedProducts: SelectedProduct[];
  onProductsChange: (products: SelectedProduct[]) => void;
  selectedShop: SelectedShop | null;
  onShopChange: (shop: SelectedShop | null) => void;
  title: string;
  onTitleChange: (title: string) => void;
  message: string;
  onMessageChange: (message: string) => void;
}

// ============ Component ============

export function StepProductSelection({
  campaignType,
  selectedProducts,
  onProductsChange,
  selectedShop,
  onShopChange,
  title,
  onTitleChange,
  message,
  onMessageChange,
}: StepProductSelectionProps) {
  const { t } = useTranslation();
  const [isPickerOpen, setIsPickerOpen] = React.useState(false);

  // Fetch shops for featured_shop campaigns
  const { data: shops = [], isLoading: isLoadingShops } = useShops();

  // Get current shop for notification preview
  const currentShop = shops[0];
  const shopLogo = currentShop?.logo ? getImageUrl(currentShop.logo) : undefined;
  const shopName = currentShop?.name || 'Ma Boutique';

  const showProductSelection = requiresProducts(campaignType);
  const showNotificationMessage = isNotificationType(campaignType);
  const showShopSelection = isFeaturedShopType(campaignType);
  const isSingleProduct = campaignType === 'push_single_product_likers';

  // Handlers for templates and emoji
  const handleTemplateSelect = (templateTitle: string, templateMessage: string) => {
    onTitleChange(templateTitle);
    onMessageChange(templateMessage);
  };

  const handleEmojiSelect = (emoji: string) => {
    onMessageChange(message + emoji);
  };

  // Get preview image based on campaign type
  const getPreviewImage = (): string | undefined => {
    if (selectedProducts.length > 0 && selectedProducts[0].image) {
      return selectedProducts[0].image;
    }
    return shopLogo;
  };

  // Determine titles based on campaign type
  const getTitle = () => {
    if (showProductSelection) {
      return t('ads.wizard.step2.titleProducts', 'Sélectionnez vos produits');
    }
    if (showNotificationMessage) {
      return t('ads.wizard.step2.titleMessage', 'Personnalisez votre message');
    }
    if (showShopSelection) {
      return t('ads.wizard.step2.titleFeaturedShop', 'Votre boutique en vedette');
    }
    return t('ads.wizard.step2.titleProducts', 'Sélectionnez vos produits');
  };

  const getSubtitle = () => {
    if (showProductSelection) {
      return isSingleProduct
        ? t('ads.wizard.step2.subtitleSingle', 'Choisissez le produit à promouvoir')
        : t('ads.wizard.step2.subtitleMultiple', 'Choisissez les produits à promouvoir');
    }
    if (showNotificationMessage) {
      return t('ads.wizard.step2.subtitleShop', 'Écrivez le message de votre notification');
    }
    if (showShopSelection) {
      return t('ads.wizard.step2.subtitleFeaturedShop', 'Vérifiez les informations de votre boutique');
    }
    return '';
  };

  // Get campaign type info
  const typeInfo = CAMPAIGN_TYPE_INFO[campaignType];
  const TypeIcon = typeInfo?.icon || Bell;

  return (
    <div className="space-y-6">
      {/* Campaign Objective Info Card */}
      <Card className="bg-primary/5 border-primary/20">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className="size-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
              <TypeIcon className="size-5 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-semibold text-sm">{typeInfo?.label || 'Campagne'}</h3>
                {typeInfo?.audienceLabel && (
                  <Badge variant="secondary" className="text-xs">
                    {typeInfo.audienceLabel}
                  </Badge>
                )}
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                {typeInfo?.objective || 'Créez votre campagne publicitaire'}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="text-center">
        <h2 className="text-xl font-semibold mb-2">{getTitle()}</h2>
        <p className="text-muted-foreground">{getSubtitle()}</p>
      </div>

      {/* Product selection */}
      {showProductSelection && (
        <div className="space-y-4">
          {/* Requirement indicator */}
          {selectedProducts.length === 0 && (
            <div className="bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
              <div className="flex items-center gap-2 text-amber-800 dark:text-amber-200">
                <Info className="size-5 shrink-0" />
                <span className="font-medium">
                  {isSingleProduct
                    ? 'Sélectionnez un produit pour continuer'
                    : 'Sélectionnez au moins un produit pour continuer'}
                </span>
              </div>
            </div>
          )}

          {/* Selected products display */}
          {selectedProducts.length > 0 && (
            <div className="space-y-2">
              <Label className="text-sm font-medium">
                {selectedProducts.length === 1
                  ? '1 produit sélectionné'
                  : `${selectedProducts.length} produits sélectionnés`}
              </Label>
              <div className="grid gap-2 sm:grid-cols-2">
                {selectedProducts.map((product) => (
                  <div
                    key={product.refId}
                    className="flex items-center gap-3 p-3 rounded-lg border bg-muted/30"
                  >
                    {product.image ? (
                      <img
                        src={product.image}
                        alt={product.name}
                        className="size-12 rounded object-cover shrink-0"
                      />
                    ) : (
                      <div className="size-12 rounded bg-muted flex items-center justify-center shrink-0">
                        <Package className="size-5 text-muted-foreground" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium line-clamp-1">{product.name}</p>
                      {product.price && (
                        <p className="text-sm text-muted-foreground">
                          {formatPrice(product.price, 'EUR')}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Add/Change products button */}
          <Button
            type="button"
            variant={selectedProducts.length > 0 ? 'outline' : 'default'}
            size="lg"
            className="w-full"
            onClick={() => setIsPickerOpen(true)}
          >
            <Package className="size-4 mr-2" />
            {selectedProducts.length > 0
              ? t('ads.wizard.step2.changeProducts', 'Modifier la sélection')
              : t('ads.wizard.step2.selectProducts', 'Sélectionner des produits')}
          </Button>

          <ProductPickerDialog
            open={isPickerOpen}
            onOpenChange={setIsPickerOpen}
            multiSelect={!isSingleProduct}
            onSelect={isSingleProduct ? (p) => onProductsChange([p]) : undefined}
            onSelectMultiple={!isSingleProduct ? onProductsChange : undefined}
            selectedProductIds={selectedProducts.map((p) => p.refId)}
          />
        </div>
      )}

      {/* Message for notifications */}
      {showNotificationMessage && (
        <div className="space-y-4">
          {/* Message requirement indicator */}
          {!message.trim() && (
            <div className="bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
              <div className="flex items-center gap-2 text-amber-800 dark:text-amber-200">
                <Info className="size-5 shrink-0" />
                <span className="font-medium">
                  Écrivez un message pour votre notification
                </span>
              </div>
            </div>
          )}

          {/* Title field (optional) */}
          <div className="space-y-2">
            <Label htmlFor="title">
              {t('ads.wizard.step2.title', 'Titre')}
              <span className="text-muted-foreground text-xs ml-1">(optionnel)</span>
            </Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => onTitleChange(e.target.value)}
              placeholder="Ex: Nouvelle collection !"
              maxLength={50}
            />
            <p className="text-xs text-muted-foreground text-right">
              {title.length}/50 caractères
            </p>
          </div>

          {/* Message field */}
          <div className="space-y-2">
            <Label htmlFor="message">
              {t('ads.wizard.step2.message', 'Message de la notification')}
              <span className="text-destructive ml-1">*</span>
            </Label>
            <Textarea
              id="message"
              value={message}
              onChange={(e) => onMessageChange(e.target.value)}
              placeholder={t(
                'ads.wizard.step2.messagePlaceholder',
                'Ex: Nouvelle collection disponible ! Découvrez nos dernières nouveautés...'
              )}
              rows={4}
              maxLength={200}
            />
            <div className="flex justify-between items-center">
              <span className="text-xs text-muted-foreground">
                {message.length}/200 caractères
              </span>
              <EmojiPickerButton onEmojiSelect={handleEmojiSelect} />
            </div>
          </div>

          {/* Quick templates */}
          <div className="space-y-2">
            <Label className="text-sm font-medium flex items-center gap-1">
              💡 Templates rapides
            </Label>
            <MessageTemplates onSelect={handleTemplateSelect} />
          </div>

          {/* Live Preview with NotificationPreview */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">📱 Aperçu en temps réel</Label>
            <NotificationPreview
              shopName={shopName}
              shopLogo={shopLogo}
              title={title}
              message={message}
              image={getPreviewImage()}
              products={selectedProducts.length > 1 ? selectedProducts : undefined}
            />
          </div>
        </div>
      )}

      {/* Featured Shop - Shop selection */}
      {showShopSelection && (
        <div className="space-y-4">
          {/* Requirement indicator */}
          {!selectedShop && (
            <div className="bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
              <div className="flex items-center gap-2 text-amber-800 dark:text-amber-200">
                <Info className="size-5 shrink-0" />
                <span className="font-medium">
                  Sélectionnez la boutique à promouvoir
                </span>
              </div>
            </div>
          )}

          {/* Shop list */}
          {isLoadingShops ? (
            <div className="space-y-3">
              {[1, 2].map((i) => (
                <Skeleton key={i} className="h-24 w-full rounded-xl" />
              ))}
            </div>
          ) : shops.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="pt-6 text-center">
                <Store className="size-12 mx-auto text-muted-foreground mb-3" />
                <p className="font-medium">Aucune boutique trouvée</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Vous devez d'abord créer une boutique pour utiliser cette
                  fonctionnalité.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              <Label className="text-sm font-medium">
                {shops.length === 1 ? 'Votre boutique' : 'Sélectionnez une boutique'}
              </Label>
              <div className="grid gap-3">
                {shops.map((shop: Shop) => {
                  const isSelected = selectedShop?.id === shop.id;
                  const logoUrl = shop.logo ? getImageUrl(shop.logo) : undefined;
                  return (
                    <button
                      key={shop.id}
                      type="button"
                      onClick={() =>
                        onShopChange({
                          id: shop.id,
                          name: shop.name,
                          logo: logoUrl,
                          productCount: shop.productCount ?? shop.totalProducts,
                          rating: shop.rating,
                        })
                      }
                      className={cn(
                        'relative flex items-center gap-4 p-4 rounded-xl border-2 transition-all text-left w-full',
                        isSelected
                          ? 'border-primary bg-primary/5 ring-2 ring-primary/20'
                          : 'border-border hover:border-primary/50 hover:bg-muted/50'
                      )}
                    >
                      {isSelected && (
                        <div className="absolute top-3 right-3">
                          <Check className="size-5 text-primary" />
                        </div>
                      )}
                      {logoUrl ? (
                        <img
                          src={logoUrl}
                          alt={shop.name}
                          className="size-14 rounded-xl object-cover shrink-0"
                        />
                      ) : (
                        <div className="size-14 rounded-xl bg-muted flex items-center justify-center shrink-0">
                          <Store className="size-6 text-muted-foreground" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-lg">{shop.name}</p>
                        <div className="flex items-center gap-3 mt-1 text-sm text-muted-foreground">
                          {(shop.productCount ?? shop.totalProducts) !== undefined && (
                            <span>
                              {shop.productCount ?? shop.totalProducts} produits
                            </span>
                          )}
                          {shop.rating !== undefined && shop.rating > 0 && (
                            <span className="flex items-center gap-1">
                              {shop.rating.toFixed(1)}
                            </span>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Info about what will be displayed */}
          {selectedShop && (
            <div className="space-y-3 pt-4 border-t">
              <h4 className="font-medium text-sm text-muted-foreground">
                Ce qui sera affiché :
              </h4>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="flex items-center gap-3 p-3 rounded-lg border bg-muted/30">
                  <Check className="size-5 text-green-600 shrink-0" />
                  <span className="text-sm">Logo et nom de la boutique</span>
                </div>
                <div className="flex items-center gap-3 p-3 rounded-lg border bg-muted/30">
                  <Check className="size-5 text-green-600 shrink-0" />
                  <span className="text-sm">Nombre de produits</span>
                </div>
                <div className="flex items-center gap-3 p-3 rounded-lg border bg-muted/30">
                  <Check className="size-5 text-green-600 shrink-0" />
                  <span className="text-sm">Note et avis clients</span>
                </div>
                <div className="flex items-center gap-3 p-3 rounded-lg border bg-muted/30">
                  <Check className="size-5 text-green-600 shrink-0" />
                  <span className="text-sm">Badge "En vedette"</span>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
